import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast, ToastContainer } from '../../components/common/Toast'
import apiService from '../../services/apiService'

const UserManagement = () => {
  const navigate = useNavigate()
  const { toasts, removeToast, success, error } = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [togglingUserId, setTogglingUserId] = useState(null)

  const token = localStorage.getItem('tenant_admin_token')

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)

      if (!token) {
        error('Authentication token missing. Please login again.')
        navigate('/admin/login')
        return
      }

      const response = await apiService.get('/client-admin/users')
      if (response.data.success) {
        setUsers(response.data.data)
      }
    } catch (err) {
      console.error('Error fetching users:', err)

      if (err.response?.status === 401) {
        error('Your session has expired. Please login again.')
        localStorage.removeItem('tenant_admin_token')
        localStorage.removeItem('tenant_admin_user')
        navigate('/admin/login')
      } else if (err.response?.status === 404) {
        error('Tenant not found. Please check your URL.')
      } else {
        const errorMsg = err.response?.data?.message || 'Failed to fetch users'
        error(errorMsg)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    const action = newStatus === 'active' ? 'activate' : 'deactivate'
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return

    try {
      setTogglingUserId(userId)
      await apiService.patch(`/client-admin/users/${userId}/status`, { status: newStatus })
      success(`User ${action}d successfully`)
      fetchUsers()
    } catch (err) {
      console.error(`Error ${action} user:`, err)
      error(`Failed to ${action} user`)
    } finally {
      setTogglingUserId(null)
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = (
      user.association_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.president?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.secretary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.official_email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    const matchesStatus = !filterStatus || user.status === filterStatus
    return matchesSearch && matchesStatus
  })

  return (
    <div className="user-management-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-users-cog"></i>
          </div>
          <div className="header-text">
            <h1>User Management</h1>
            <p>Manage association users and their information</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            className="btn-add-user"
            onClick={() => navigate('/admin/users/add')}
          >
            <i className="fas fa-plus"></i> Add New User
          </button>
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
            }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-icon total">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">{users.length}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon active">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">{users.filter(u => u.status === 'active').length}</span>
            <span className="stat-label">Active Users</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon suspended">
            <i className="fas fa-user-slash"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">{users.filter(u => u.status === 'inactive').length}</span>
            <span className="stat-label">Inactive Users</span>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="content-section">
        <div className="filters-section">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search by association name, president, secretary, username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Association Name</th>
                  <th>State</th>
                  <th>President</th>
                  <th>Secretary</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      <div className="no-data-content">
                        <i className="fas fa-inbox"></i>
                        <p>No users found</p>
                        <span>Try adjusting your search or filter</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr key={user.id}>
                      <td className="sno-cell">{index + 1}</td>
                      <td>
                        <div className="user-name-cell">
                          <strong>{user.association_name || '-'}</strong>
                          <small>@{user.username}</small>
                        </div>
                      </td>
                      <td>
                        <span className="state-badge">{user.state || '-'}</span>
                      </td>
                      <td>{user.president || '-'}</td>
                      <td>{user.secretary || '-'}</td>
                      <td>
                        <div className="contact-cell">
                          <span><i className="fas fa-phone"></i> {user.official_contact || '-'}</span>
                          <span><i className="fas fa-envelope"></i> {user.official_email || '-'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${user.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                          {user.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className={`btn-toggle-status ${user.status === 'active' ? 'btn-deactivate' : 'btn-activate'}`}
                            onClick={() => handleToggleStatus(user.id, user.status)}
                            title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
                            disabled={togglingUserId === user.id}
                          >
                            {togglingUserId === user.id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <>
                                <i className={`fas fa-${user.status === 'active' ? 'ban' : 'check-circle'}`}></i>
                                <span>{user.status === 'active' ? 'Deactivate' : 'Activate'}</span>
                              </>
                            )}
                          </button>
                          <button
                            className="btn-icon btn-view"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            title="View Profile"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                            title="Edit User"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .user-management-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: var(--gray-100, #f3f4f6);
        }

        .page-header-banner {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          margin-left: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: calc(100% - 20px);
          box-sizing: border-box;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .header-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .header-text h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-text p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .btn-add-user {
          padding: 0.75rem 1.5rem;
          background: #c00f15;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-add-user:hover {
          background: #a00d12;
          transform: translateY(-1px);
        }


        /* Stats Section */
        .stats-section {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          padding: 1.5rem;
          margin-top: -20px;
          position: relative;
          z-index: 10;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon i {
          font-size: 1.25rem;
        }

        .stat-icon.total {
          background: rgba(2, 42, 94, 0.1);
          color: var(--primary-color, #022a5e);
        }

        .stat-icon.active {
          background: #dcfce7;
          color: #16a34a;
        }

        .stat-icon.pending {
          background: #fef3c7;
          color: #d97706;
        }

        .stat-icon.suspended {
          background: #fee2e2;
          color: #dc2626;
        }

        .stat-info {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-800, #1f2937);
        }

        .stat-label {
          font-size: 0.8rem;
          color: var(--gray-500, #6b7280);
        }

        /* Content Section */
        .content-section {
          padding: 0 1.5rem 1.5rem;
          margin-left: 0;
        }

        .filters-section {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .search-box {
          flex: 1;
          position: relative;
        }

        .search-box i {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400, #9ca3af);
        }

        .search-box input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          border: 1px solid var(--gray-200, #e5e7eb);
          border-radius: 10px;
          font-size: 0.9rem;
          background: white;
          transition: all 0.2s;
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .filter-select {
          padding: 0.875rem 1.25rem;
          border: 1px solid var(--gray-200, #e5e7eb);
          border-radius: 10px;
          background: white;
          font-size: 0.9rem;
          min-width: 150px;
          cursor: pointer;
        }

        .filter-select:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
        }

        .users-table-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
        }

        .users-table thead {
          background: linear-gradient(135deg, #022a5e 0%, #053e88 50%, #022a5e 100%);
        }

        .users-table thead tr {
          background: transparent !important;
        }

        .users-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: white !important;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: transparent !important;
          white-space: nowrap;
        }

        .users-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--gray-100, #f1f5f9);
          vertical-align: middle;
        }

        .users-table tbody tr {
          transition: background 0.2s;
        }

        .users-table tbody tr:hover {
          background: var(--gray-50, #f8fafc);
        }

        .sno-cell {
          font-weight: 600;
          color: var(--gray-500, #6b7280);
          width: 60px;
        }

        .user-name-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .user-name-cell strong {
          color: var(--gray-800, #1f2937);
          font-weight: 600;
        }

        .user-name-cell small {
          color: var(--gray-400, #9ca3af);
          font-size: 0.75rem;
        }

        .state-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: var(--gray-100, #f1f5f9);
          color: var(--gray-600, #475569);
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .contact-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .contact-cell span {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--gray-500, #6b7280);
          font-size: 0.8rem;
        }

        .contact-cell i {
          font-size: 0.7rem;
          color: var(--gray-400, #9ca3af);
        }

        .status-badge {
          display: inline-block;
          padding: 0.35rem 0.85rem;
          border-radius: 5px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-active {
          background: #dcfce7;
          color: #166534;
        }

        .status-pending {
          background: #fef3c7;
          color: #92400e;
        }

        .status-suspended {
          background: #fee2e2;
          color: #991b1b;
        }

        .status-inactive {
          background: #c96568;
          color: #ffffff;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: nowrap;
          margin-top: -2px;
        }

        .btn-icon {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .btn-icon:hover {
          transform: translateY(-2px);
        }

        .btn-view {
          background: rgba(2, 42, 94, 0.1);
          color: var(--primary-color, #022a5e);
        }

        .btn-view:hover {
          background: rgba(2, 42, 94, 0.2);
        }

        .btn-edit {
          background: #fef3c7;
          color: #92400e;
        }

        .btn-edit:hover {
          background: #fde68a;
        }

        .btn-toggle-status {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 0 12px;
          height: 32px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .btn-toggle-status:hover {
          transform: translateY(-1px);
        }

        .btn-toggle-status:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-activate {
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #bbf7d0;
        }

        .btn-activate:hover:not(:disabled) {
          background: #dcfce7;
        }

        .btn-deactivate {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }

        .btn-deactivate:hover:not(:disabled) {
          background: #fee2e2;
        }

        .loading-state {
          text-align: center;
          padding: 4rem 2rem;
          color: var(--gray-500, #6b7280);
          background: white;
          border-radius: 12px;
        }

        .loading-state i {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          color: var(--primary-color, #022a5e);
        }

        .no-data {
          text-align: center;
          padding: 0 !important;
        }

        .no-data-content {
          padding: 4rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .no-data-content i {
          font-size: 3rem;
          color: var(--gray-300, #d1d5db);
          margin-bottom: 0.5rem;
        }

        .no-data-content p {
          margin: 0;
          font-weight: 600;
          color: var(--gray-500, #6b7280);
        }

        .no-data-content span {
          font-size: 0.85rem;
          color: var(--gray-400, #9ca3af);
        }

        @media (max-width: 1200px) {
          .stats-section {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .page-header-banner {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }

          .stats-section {
            grid-template-columns: 1fr 1fr;
          }

          .filters-section {
            flex-direction: column;
          }

          .users-table-container {
            overflow-x: auto;
          }

          .users-table {
            min-width: 900px;
          }
        }
      `}</style>
    </div>
  )
}

export default UserManagement
